import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subject, takeUntil } from 'rxjs';
import { GhostOwnershipInfoDeleteService } from './ghost-ownership-info-delete.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-ghost-ownership-info-delete',
    templateUrl: './ghost-ownership-info-delete.component.html',
    styleUrls: ['./ghost-ownership-info-delete.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GhostOwnershipInfoDeleteComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private destroy$ = new Subject<void>();

  filterParams: any = {};
  globalParams: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};

  modalTitle = 'Delete Payment';
  userSettings: any;
  logged_in_user: string = '';
  loading = false;
  
  paymentJson: any[] = [];
  clob_settings: any[] = [];

  constructor(
    private ghostOwnershipInfoDeleteService: GhostOwnershipInfoDeleteService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.gtwWorkspace.setTabActive.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    });
    this.gtwGlobal.globalParamsChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    });
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
  }

  init() {
    try {
      if (this.GENERAL_CONFIG && this.USER_SETTINGS) {
        this.userSettings = this.USER_SETTINGS;
        this.logged_in_user = this.userSettings?.user?.sso_id || '';
      }

      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupDeleteParams();
      }
    } catch (e) {
      console.error('Error in GhostOwnershipInfoDelete init:', e);
    }
  }

  private setupDeleteParams() {
    // Build payment JSON from rowData (following AngularJS pattern)
    this.paymentJson = [{
      filing_key: this.rowData.FILING_GROUP,
      ghost_scenario: this.rowData.SCENARIO,
      ho_leid: this.rowData.HO_LEID,
      ho_cdr_no: this.rowData.HO_CDR_NO,
      parent_leid: this.rowData.PARENT_LEID,
      parent_cdr_no: this.rowData.PARENT_CDR_NO,
      begin_date: this.rowData.BEGIN_DATE
    }];

    // Setup CLOB settings
    this.clob_settings = [{
      sso_id: this.logged_in_user,
      screen_key: this.globalParams?.screen_key || ''
    }];
  }

  deletePayment() {
    if (!this.paymentJson.length || !this.clob_settings.length) {
      this.gtwSnackbarService.openSnackBar('Missing required data for deletion', 'gtw-snackbar--red');
      return;
    }

    this.loading = true;

    this.ghostOwnershipInfoDeleteService.deletePayment(
      this.GENERAL_CONFIG.base_url,
      this.paymentJson,
      this.globalParams || {},
      this.clob_settings,
      this.rowData
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (data: any) => {
        this.loading = false;
        
        if (data.data?.errorMessage && data.data.errorMessage !== "null") {
          this.gtwSnackbarService.openSnackBar(data.data.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('The recipient was successfully deleted', 'gtw-snackbar--green');
          
          // Emit refresh event to AngularJS
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.loading = false;
        let errorMessage = 'Error deleting payment';
        if (error?.error?.errorMessage) {
          errorMessage += '. ' + error.error.errorMessage;
        }
        console.error("Error deleting payment:", error);
        this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
      }
    );
  }

  modalClose() {
    const customEvent = new CustomEvent('cancel-form', {
      detail: 'cancel',
      bubbles: true
    });
    document.querySelector('gtw-ghost-ownership-info-delete-ce')?.dispatchEvent(customEvent);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

